import request from '@/Network/Requests/request'
import api from '@/Network/Requests/api'
import Service from '@/Network/Services/Service'
import type {
  CreateApprovalFlowRequest,
  UpdateApprovalFlowRequest
} from '@/Network/Types/Requests/ApprovalFlowRequests'
import type { CreateApprovalFlowTemplateRequest } from '@/Network/Types/Requests/ApprovalFlowTemplateRequests'
import type { ApprovalFlowDetail } from '@/Network/Types/Responses/ApprovalFlowResponses'
import type {
  ApprovalFlowTemplateDetail,
  GroupedTemplatesResponse
} from '@/Network/Types/Responses/ApprovalFlowTemplateResponses'
import { isActiveValidationRole } from '@/types/approval-flow.types'

function sanitizeApprovalFlowResponse(flow: ApprovalFlowDetail): ApprovalFlowDetail {
  return {
    ...flow,
    layers: flow.layers.map((layer) => ({
      ...layer,
      roles: layer.roles.filter((r) => {
        if (!isActiveValidationRole(r.role)) {
          console.warn('[ApprovalFlowService] role legado ignorado na response:', r.role)
          return false
        }
        return true
      })
    }))
  }
}

export default class ApprovalFlowService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/approval-flows'
  }

  async create(data: CreateApprovalFlowRequest): Promise<ApprovalFlowDetail> {
    try {
      const response = (await request.post(this.getCompletePath(), data)) as {
        data: ApprovalFlowDetail
      }
      return sanitizeApprovalFlowResponse(response.data)
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível criar o fluxo de aprovação.')
    }
  }

  async getBySchedule(scheduleId: number): Promise<ApprovalFlowDetail | null> {
    try {
      const response = (await request.get(`${this.getCompletePath()}/schedule/${scheduleId}`)) as {
        data: ApprovalFlowDetail | null
      }
      if (response.data) return sanitizeApprovalFlowResponse(response.data)
      return null
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível carregar o fluxo de aprovação da escala.')
    }
  }

  async update(id: number, data: UpdateApprovalFlowRequest): Promise<ApprovalFlowDetail> {
    try {
      const response = (await request.put(`${this.getCompletePath()}/${id}`, data)) as {
        data: ApprovalFlowDetail
      }
      return sanitizeApprovalFlowResponse(response.data)
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível atualizar o fluxo de aprovação.')
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await request.delete(`${this.getCompletePath()}/${id}`)
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível remover o fluxo de aprovação.')
    }
  }

  async getTemplatesBySchedule(scheduleId: number): Promise<ApprovalFlowTemplateDetail[]> {
    try {
      const response = (await request.get(`/approval-flow-templates/schedule/${scheduleId}`)) as {
        data: ApprovalFlowTemplateDetail[]
      }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível carregar os modelos de configuração da escala.')
    }
  }

  async getTemplatesGroupedByCompany(companyId: number): Promise<GroupedTemplatesResponse[]> {
    try {
      const response = (await request.get(
        `/approval-flow-templates/company/${companyId}/grouped`
      )) as {
        data: GroupedTemplatesResponse[]
      }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(
        e,
        'Não foi possível carregar os modelos de configuração agrupados por unidade e escala.'
      )
    }
  }

  async deleteTemplate(id: number, companyId: number): Promise<void> {
    try {
      await api.delete(`/approval-flow-templates/${id}`, { data: { companyId } })
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível excluir o modelo de configuração.')
    }
  }

  async createTemplate(
    data: CreateApprovalFlowTemplateRequest
  ): Promise<ApprovalFlowTemplateDetail> {
    try {
      const response = (await request.post('/approval-flow-templates', data)) as {
        data: ApprovalFlowTemplateDetail
      }
      return response.data
    } catch (e: unknown) {
      throw this.handleError(e, 'Não foi possível criar o modelo de configuração.')
    }
  }
}
